import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ImagePlus, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, StatusBadge } from "@/components/common";
import { PhotoGrid } from "@/modules/interventions/shared";
import { PlanNotice } from "@/components/PlanGate";
import { useGarage } from "@/context/GarageContext";
import { api, errMsg } from "@/lib/api";
import { fmtCents, fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

export async function run(fn, onDone, msg = t("common.saved")) {
  try {
    const { data } = await fn();
    onDone(data);
    toast.success(msg);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

const STATE_CLS = { ok: "bg-emerald-600 text-white", attention: "bg-amber-500 text-white", urgent: "bg-red-600 text-white", na: "bg-slate-500 text-white" };

export function ChecklistCard({ job, setJob, readOnly, legacy }) {
  const [items, setItems] = useState(job.checklist);
  const set = (id, patch) => setItems(items.map((i) => (i.item_id === id ? { ...i, ...patch } : i)));
  const sections = [...new Set(items.map((i) => i.section))];
  const save = () => run(() => api.put(`/jobs/${job.id}/checklist`, { items: items.map(({ item_id, state, note }) => ({ item_id, state, note })) }), setJob);
  return (
    <Card testId="job-checklist-card">
      <SectionTitle hint={legacy ? t("b14.legacyHint") : t("jobs.checklistHint")}>{legacy ? t("b14.legacyTitle") : t("jobs.checklist")}</SectionTitle>
      {sections.map((s) => (
        <div key={s} className="mb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{s}</p>
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {items.filter((i) => i.section === s).map((i) => (
              <div key={i.item_id} className="flex flex-wrap items-center gap-2 p-2 text-sm" data-testid={`check-item-${i.item_id}`}>
                <span className="min-w-40 flex-1 font-medium">{i.label}</span>
                <div className="flex gap-1">
                  {["ok", "attention", "urgent", "na"].map((st) => (
                    <button key={st} type="button" disabled={readOnly} onClick={() => set(i.item_id, { state: i.state === st ? null : st })} data-testid={`check-${i.item_id}-${st}`}
                      className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors duration-150 ${i.state === st ? STATE_CLS[st] : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t(`checklist.${st}`)}</button>
                  ))}
                </div>
                <Input value={i.note} disabled={readOnly} onChange={(e) => set(i.item_id, { note: e.target.value })} placeholder={t("checklist.note")} className="h-8 w-full sm:w-56" />
              </div>
            ))}
          </div>
        </div>
      ))}
      {!readOnly && <Button onClick={save} data-testid="checklist-save">{t("common.save")}</Button>}
    </Card>
  );
}

export function ObservationsCard({ job, setJob, readOnly }) {
  const [text, setText] = useState("");
  const [shared, setShared] = useState(true);
  const add = (e) => {
    e.preventDefault();
    run(() => api.post(`/jobs/${job.id}/observations`, { text, shared }), (d) => { setJob(d); setText(""); });
  };
  return (
    <Card testId="job-observations-card">
      <SectionTitle hint={t("jobs.observationsHint")}>{t("jobs.observations")}</SectionTitle>
      <div className="space-y-2">
        {job.observations.length === 0 && <p className="text-sm text-slate-500">{t("jobs.noObservations")}</p>}
        {job.observations.map((o) => (
          <div key={o.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid={`observation-${o.id}`}>
            <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.shared ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"}`}>{o.shared ? t("jobs.shared") : t("jobs.internal")}</span>
            <p className="flex-1 whitespace-pre-wrap">{o.text}<span className="block text-[11px] text-slate-400">{o.by} · {fmtDateTime(o.at)}</span></p>
            {!readOnly && <>
              <Button size="icon" variant="ghost" onClick={() => run(() => api.put(`/jobs/${job.id}/observations/${o.id}`, { shared: !o.shared }), setJob)} data-testid={`observation-toggle-${o.id}`} aria-label={t("jobs.toggleShare")}>{o.shared ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
              <Button size="icon" variant="ghost" className="text-red-600" onClick={() => run(() => api.post(`/jobs/${job.id}/observations/${o.id}/remove`), setJob)} data-testid={`observation-remove-${o.id}`} aria-label={t("common.remove")}><Trash2 className="h-4 w-4" /></Button>
            </>}
          </div>
        ))}
      </div>
      {!readOnly && (
        <form onSubmit={add} className="mt-4 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("jobs.observationPlaceholder")} data-testid="observation-input" />
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={shared} onCheckedChange={setShared} data-testid="observation-shared-switch" /> {t("jobs.shareWithCustomer")}</label>
            <Button type="submit" disabled={!text.trim()} data-testid="observation-add"><Plus className="mr-1 h-4 w-4" /> {t("avail.add")}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

export function NotesCard({ job, setJob, readOnly }) {
  const [text, setText] = useState("");
  return (
    <Card className="border-slate-300 bg-slate-50" testId="job-notes-card">
      <SectionTitle hint={t("jobs.notesHint")}><span className="inline-flex items-center gap-2"><Lock className="h-4 w-4" /> {t("jobs.notes")}</span></SectionTitle>
      <div className="space-y-2">
        {job.internal_notes.map((n) => <p key={n.id} className="whitespace-pre-wrap rounded-lg bg-white p-3 text-sm" data-testid={`note-${n.id}`}>{n.text}<span className="block text-[11px] text-slate-400">{n.by} · {fmtDateTime(n.at)}</span></p>)}
      </div>
      {!readOnly && <div className="mt-3 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("jobs.notePlaceholder")} data-testid="note-input" />
        <Button disabled={!text.trim()} onClick={() => run(() => api.post(`/jobs/${job.id}/notes`, { text }), (d) => { setJob(d); setText(""); })} data-testid="note-add">{t("avail.add")}</Button>
      </div>}
    </Card>
  );
}

export function PhotosCard({ job, setJob, readOnly }) {
  const { hasFeature } = useGarage();
  const [meta, setMeta] = useState({ phase: "before", caption: "", shared: false });
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, String(v)));
    setBusy(true);
    await run(() => api.post(`/jobs/${job.id}/photos`, fd), (d) => { setJob(d); setMeta({ ...meta, caption: "" }); }, t("jobs.photoAdded"));
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <Card testId="job-photos-card">
      <SectionTitle hint={t("jobs.photosHint")}>{t("jobs.photos")}</SectionTitle>
      {!hasFeature("photo_storage") && <div className="mb-4"><PlanNotice feature="photo_storage" /></div>}
      {!readOnly && hasFeature("photo_storage") && (
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-3">
          <Select value={meta.phase} onValueChange={(v) => setMeta({ ...meta, phase: v })}>
            <SelectTrigger className="w-32" data-testid="photo-phase-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["before", "after"].map((p) => <SelectItem key={p} value={p}>{t(`status.${p}`)}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} placeholder={t("jobs.caption")} className="w-56" data-testid="photo-caption-input" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={meta.shared} onCheckedChange={(v) => setMeta({ ...meta, shared: v })} data-testid="photo-shared-switch" /> {t("jobs.visibleToCustomer")}</label>
          <Button variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid="photo-upload-button"><ImagePlus className="mr-2 h-4 w-4" /> {busy ? t("common.uploading") : t("book.addPhoto")}</Button>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="photo-file-input" />
        </div>
      )}
      <PhotoGrid photos={job.photos} testId="job-photo-grid" render={(p) => (
        <>
          {p.caption && <p className="text-slate-700">{p.caption}</p>}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5"><Switch checked={p.shared} disabled={readOnly} onCheckedChange={(v) => run(() => api.put(`/jobs/${job.id}/photos/${p.id}`, { shared: v }), setJob)} data-testid={`photo-share-${p.id}`} /> {t("jobs.customer")}</label>
            {!readOnly && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => run(() => api.delete(`/jobs/${job.id}/photos/${p.id}`), setJob)} data-testid={`photo-delete-${p.id}`} aria-label={t("common.remove")}><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
        </>
      )} />
    </Card>
  );
}

export function QuotesCard({ job, readOnly }) {
  const navigate = useNavigate();
  const { hasFeature, isManager } = useGarage();
  if (!hasFeature("quotes_and_invoices")) return <Card testId="job-quotes-card"><SectionTitle>{t("quotes.title")}</SectionTitle><PlanNotice feature="quotes_and_invoices" /></Card>;
  const toInvoice = job.quotes.filter((q) => q.status === "accepted").map((q) => q.id);
  const accepted = job.quotes.find((q) => q.status === "accepted" && !q.supplementary);
  const open = ["completed", "cancelled"].includes(job.status) ? false : !readOnly;
  const create = (supplementary_to) => run(() => api.post("/quotes", { job_id: job.id, supplementary_to }), (q) => navigate(`/pro/quotes/${q.id}`), t("quotes.created"));
  return (
    <Card testId="job-quotes-card">
      <SectionTitle>{t("quotes.title")}</SectionTitle>
      {job.quotes.length === 0 && <p className="text-sm text-slate-500">{t("quotes.noneForJob")}</p>}
      <div className="space-y-2">
        {job.quotes.map((q) => (
          <button key={q.id} type="button" onClick={() => navigate(`/pro/quotes/${q.id}`)} className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left text-sm hover:bg-slate-50" data-testid={`job-quote-${q.number}`}>
            <span><span className="font-mono font-semibold">{q.number}</span> v{q.version}{q.supplementary && <span className="ml-2 text-xs text-slate-500">{t("quotes.supplementary")}</span>}</span>
            <span className="flex items-center gap-2"><strong>{fmtCents(q.total_cents)}</strong><StatusBadge status={q.status} /></span>
          </button>
        ))}
      </div>
      {open && <div className="mt-4 flex flex-wrap gap-2">
        {!accepted && <Button onClick={() => create(null)} data-testid="job-new-quote">{t("quotes.new")}</Button>}
        {accepted && <Button variant="outline" onClick={() => create(accepted.id)} data-testid="job-supplementary-quote">{t("quotes.newSupplementary")}</Button>}
      </div>}
      {isManager && !readOnly && toInvoice.length > 0 && <Button className="mt-3" variant="secondary" onClick={() => run(() => api.post("/invoices", { quote_ids: toInvoice }), (inv) => navigate(`/pro/invoices/${inv.id}`), t("invoices.created"))} data-testid="job-create-invoice">{t("invoices.fromAccepted", { n: toInvoice.length })}</Button>}
    </Card>
  );
}

export function LogCard({ log, tz }) {
  return (
    <Card testId="job-log-card">
      <SectionTitle>{t("jobs.log")}</SectionTitle>
      <ol className="max-h-80 space-y-2 overflow-y-auto text-xs">
        {log.map((e, i) => (
          <li key={i} className="border-l-2 border-slate-200 pl-3">
            <p className="font-semibold text-slate-800">{t(`jobs.actions.${e.action}`)}{e.details?.to ? ` → ${t(`status.${e.details.to}`)}` : ""}</p>
            {e.details?.note && <p className="text-slate-600">{e.details.note}</p>}
            <p className="text-slate-400">{e.by} · {fmtDateTime(e.at, tz)}</p>
          </li>
        ))}
      </ol>
    </Card>
  );
}
